import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { combineLatest, distinctUntilChanged, map, Observable, shareReplay, startWith } from 'rxjs';
import { DemoService } from 'src/app/services/demo/demo.service';
import { shouldBypassExecutiveDashboardUpsell } from 'src/app/services/demo/demo-mock-data-visibility';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { AuthState } from 'src/app/store/auth/auth.state';
import { ExecutiveCtasState } from '../../types/executive-ctas';

@Injectable({ providedIn: 'root' })
export class ExecutiveDashboardVisibilityService {
  private readonly company$ = this.store.select(AuthState.company);
  private readonly pricingPlanCategory$ = this.store.select(AuthState.pricingPlanCategory);

  private readonly dashboardFlag$ = this.growthBook.getFeature<boolean>('executive-dashboard', false);
  private readonly upsellFlag$ = this.growthBook.getFeature<boolean>('executive-dashboard-upsell-page', false);

  readonly showDashboard$: Observable<boolean> = combineLatest([
    this.company$.pipe(startWith(null)),
    this.pricingPlanCategory$.pipe(startWith(null)),
    this.dashboardFlag$.pipe(startWith(false)),
    this.demoService.showExecutiveDemoData$,
  ]).pipe(
    map(([company, planCategory, flag, showExecutiveDemoData]) => {
      if (shouldBypassExecutiveDashboardUpsell(showExecutiveDemoData)) {
        return true;
      }
      if (!flag) return false;
      if (['basic', 'standard'].includes((planCategory as string) ?? '')) {
        return false;
      }
      const userCount = company?.userCount ?? 0;
      return userCount >= 20;
    }),
    distinctUntilChanged(),
    shareReplay(1),
  );

  private readonly defaultCtasState: ExecutiveCtasState = {
    isBasicOrStandard: false,
    moreThan50: false,
    fiftyOrLess: false,
    premiumUnder20: false,
    aboveTwenty: false,
  };

  readonly upgradeCtas$: Observable<ExecutiveCtasState> = combineLatest([
    this.company$.pipe(startWith(null)),
    this.pricingPlanCategory$.pipe(startWith(null)),
  ]).pipe(
    map(([company, planCategory]) => {
      const userCount = company?.userCount ?? 0;
      const isBasicOrStandard = ['basic', 'standard'].includes((planCategory as string) ?? '');
      const isPremium = planCategory === 'premium';
      return {
        isBasicOrStandard,
        moreThan50: isBasicOrStandard && userCount > 50,
        fiftyOrLess: isBasicOrStandard && userCount <= 50,
        premiumUnder20: isPremium && userCount < 20,
        aboveTwenty: isBasicOrStandard && userCount > 20,
      } as ExecutiveCtasState;
    }),
    distinctUntilChanged((prev, curr) =>
      prev.isBasicOrStandard === curr.isBasicOrStandard &&
      prev.moreThan50 === curr.moreThan50 &&
      prev.fiftyOrLess === curr.fiftyOrLess &&
      prev.premiumUnder20 === curr.premiumUnder20 &&
      prev.aboveTwenty === curr.aboveTwenty,
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly showUpsell$: Observable<boolean> = combineLatest([
    this.upsellFlag$.pipe(startWith(false)),
    this.showDashboard$,
    this.upgradeCtas$,
  ]).pipe(
    map(([upsellFlag, showDashboard, upgradeCtas]) => {
      if (showDashboard) return false;
      if (!upsellFlag) return false;
      return upgradeCtas.premiumUnder20 || upgradeCtas.moreThan50 || upgradeCtas.fiftyOrLess;
    }),
    distinctUntilChanged(),
    shareReplay(1),
  );

  constructor(
    private readonly store: Store,
    private readonly growthBook: GrowthBookService,
    private readonly demoService: DemoService,
  ) {}
}
